// EmailLog + SystemStatus — the two Admin screens the rebuild was missing
// (docs/REBUILD_PARITY.md "Email log" and "Database status").
//
// The contract, read off the handlers rather than off the classic screens:
//
//   GET /api/admin/email-log?category=&limit=     server/routes/admin.js:14
//       authMiddleware + checkRole('admin', 'manager') — a WORKER and
//       ACCOUNTING are both 403 here, which cuts against every other route in
//       that file (adminRoutes.test.js:241 says so on purpose).
//       Answers a bare JSON ARRAY, newest first, of whole email_log rows
//       (server/database.js:781): id, recipient, from_email, subject,
//       category, related_type, related_id, status, message_id, error,
//       created_at. No envelope, no cursor, no total — `limit` is the only
//       paging there is: default 100, clamped to 500, and 0 / a negative / an
//       unparseable value all fall back to the default rather than answering
//       an empty log (adminRoutes.test.js:259-277).
//
//   GET /api/admin/integrations                   server/server.js:347
//       authMiddleware + `req.user.role !== 'admin'` → 403 { error: 'Admin
//       only' }. Answers { integrations: { <key>: { name, configured,
//       missing[], repo, effect, fix } } }. Booleans only, never a secret —
//       integrationStatus.test.js enforces that server-side. Reads environment
//       variables and nothing else, and its own comment says it is "cheap
//       enough to call on every Admin page load", so it loads with the screen.
//
//   GET /api/admin/db-status                      server/server.js:350
//       Same admin-only gate. Answers { counts, database, integrations }.
//       It runs PRAGMA integrity_check, which the handler's own comment calls
//       "expensive on a real database, and gated behind a 'Run health check'
//       button for that reason". So it is behind a button here too. Do not
//       move it onto page load.
//
// ── What is on screen, and what is deliberately not ─────────────────────────
//
// email_log holds RECIPIENT ADDRESSES and SUBJECTS. It holds no message
// bodies — there is no body column (server/database.js:781-793) — so nothing
// on this screen can leak the contents of a client's invoice email. The
// addresses are the point of the screen ("did that invoice actually reach
// them?") and the route is already admin-or-manager, the same audience the
// classic Communication screen had. Nothing extra is masked, and nothing is
// rendered that the server did not send.
//
// ── A refusal must never render as data ─────────────────────────────────────
//
// docs/AGENT_LEARNINGS.md (2026-08-17): the repeated defect here is a screen
// showing 0 — or an empty table, or "Configured" — over a request that never
// answered. "(0)" reads as arithmetic and arithmetic is trusted. Every figure
// below therefore lives inside a `query.data ? …` branch, so a 403 shows the
// server's own sentence and NO number at all, while a genuinely empty log
// still says it is empty. Both halves are pinned in OfficeEmailLog.test.js;
// deleting the second half is how this family gets "fixed" into a screen that
// cries wolf.

import React, { useMemo, useState } from 'react';
import { serverSentence } from '../../shared/http';
import { useRole } from '../../../hooks/usePermission';
import { DataTable, Pill, Stat, Notice, Skeleton, EmptyState, FwButton, FwField, FwSelect } from '../../../ui/fw';
import {
  useOfficeQuery, PageHeader, Toolbar, Panel, Split, DetailList, DetailRow, textOr,
} from '../records/OfficePage';
import { fmtDateTime } from '../shared/format';
import './admin.css';

// ── email log ───────────────────────────────────────────────────────────────

/** The categories server/database.js:786 lists for email_log.category. */
export const EMAIL_CATEGORIES = [
  { value: '', label: 'All categories' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'estimate', label: 'Estimates' },
  { value: 'worker_digest', label: 'Worker digests' },
  { value: 'notification', label: 'Notifications' },
  { value: 'test', label: 'Test emails' },
];

/** What the handler does with ?limit=, so the picker cannot offer an unreachable one. */
export const EMAIL_LIMIT_DEFAULT = 100;
export const EMAIL_LIMIT_MAX = 500;
export const EMAIL_LIMITS = [100, 200, 500];

const CATEGORY_TONE = { invoice: 'sched', estimate: 'neutral', worker_digest: 'ok', notification: 'neutral', test: 'warn' };

// Only 'sent' and 'failed' are ever written today (server/utils/email.js), so
// the third branch is the screen being right in advance. It matters anyway:
// a two-way switch on `status === 'sent'` would render a queued send as
// "Failed" and count it in neither total, on the screen whose whole job is
// answering "did that email go out?". Same reasoning as Communication.js.
const STATUS_TONE = { sent: 'ok', failed: 'stop' };
const STATUS_LABEL = { sent: 'Sent', failed: 'Failed' };

/** The request path for a filter. Exported so a test can pin the query string. */
export function emailLogPath(category, limit) {
  const params = new URLSearchParams();
  params.set('limit', String(EMAIL_LIMITS.includes(Number(limit)) ? Number(limit) : EMAIL_LIMIT_DEFAULT));
  if (category) params.set('category', category);
  return `/admin/email-log?${params.toString()}`;
}

/**
 * How many of each status, in a fixed order with every other status kept.
 * Exported because it is the thing that must never be shown over a refusal,
 * and a pure function is the honest place to test that it sums to the rows.
 */
export function statusTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const out = [];
  const seen = new Map();
  for (const r of list) {
    const key = r && r.status ? String(r.status) : 'unknown';
    seen.set(key, (seen.get(key) || 0) + 1);
  }
  for (const key of ['sent', 'failed']) {
    if (seen.has(key)) { out.push({ key, label: STATUS_LABEL[key], count: seen.get(key), tone: STATUS_TONE[key] }); seen.delete(key); }
  }
  for (const [key, count] of seen) out.push({ key, label: key, count, tone: 'neutral' });
  return out;
}

function EmailDetail({ row, onClose }) {
  return (
    <Panel
      title="This email"
      actions={<FwButton variant="ghost" icon="x" aria-label="Close" onClick={onClose} />}
    >
      <DetailList>
        <DetailRow label="Status">
          <Pill tone={STATUS_TONE[row.status] || 'neutral'}>{STATUS_LABEL[row.status] || textOr(row.status)}</Pill>
        </DetailRow>
        <DetailRow label="To">{textOr(row.recipient)}</DetailRow>
        <DetailRow label="From">{textOr(row.from_email)}</DetailRow>
        <DetailRow label="Subject">{textOr(row.subject)}</DetailRow>
        <DetailRow label="Category">{textOr(row.category)}</DetailRow>
        <DetailRow label="About">
          {row.related_type && row.related_id != null ? `${row.related_type} #${row.related_id}` : '—'}
        </DetailRow>
        <DetailRow label="Sent at">{fmtDateTime(row.created_at)}</DetailRow>
        <DetailRow label="Message id">{textOr(row.message_id)}</DetailRow>
      </DetailList>
      {row.error ? <pre className="or-log" data-testid="email-error">{row.error}</pre> : null}
    </Panel>
  );
}

export default function EmailLog() {
  const role = useRole();
  const mayRead = role === 'admin' || role === 'manager';
  const [category, setCategory] = useState('');
  const [limit, setLimit] = useState(EMAIL_LIMIT_DEFAULT);
  const [selectedId, setSelectedId] = useState(null);

  // The key carries the filter, so switching filters starts a NEW query rather
  // than overwriting the old one's rows. That is what makes a slow first
  // request unable to land under a second filter — the race Communication.js
  // hand-rolls a sequence number for.
  const log = useOfficeQuery(
    ['office', 'email-log', category, limit],
    emailLogPath(category, limit),
    { enabled: mayRead },
  );

  const rows = useMemo(() => (Array.isArray(log.data) ? log.data : []), [log.data]);
  const selected = rows.find((r) => r.id === selectedId) || null;

  if (!mayRead) {
    return (
      <div className="or-page ad-page" data-screen="office-EmailLog">
        <PageHeader title="Email log" />
        <EmptyState icon="lock" title="Not available to this role">
          The email log is limited to admins and managers.
        </EmptyState>
      </div>
    );
  }

  // The table reads chronologically — Sent first — and the card does not: on a
  // phone the SUBJECT is what identifies an email, the recipient is the line
  // under it, and sent/failed/bounced is the badge, because "did it go" is the
  // only reason anybody opens this log. Sent and Category stay as facts; the
  // related record ("invoice #12") is table-only, since the category already
  // says what kind of thing it was about.
  //
  // The `width` hints are the table's; a card has no columns to size.
  const columns = [
    { key: 'created_at', label: 'Sent', width: '160px', role: 'fact', render: (r) => fmtDateTime(r.created_at) },
    { key: 'status', label: 'Status', width: '90px', role: 'badge', render: (r) => (
      <Pill tone={STATUS_TONE[r.status] || 'neutral'}>{STATUS_LABEL[r.status] || textOr(r.status)}</Pill>
    ) },
    { key: 'recipient', label: 'To', role: 'subtitle', render: (r) => textOr(r.recipient) },
    { key: 'subject', label: 'Subject', role: 'title', render: (r) => textOr(r.subject) },
    { key: 'category', label: 'Category', width: '130px', role: 'fact', render: (r) => (
      r.category ? <Pill tone={CATEGORY_TONE[r.category] || 'neutral'}>{String(r.category).replace(/_/g, ' ')}</Pill> : '—'
    ) },
    { key: 'related', label: 'About', width: '130px', role: 'hidden', render: (r) => (
      r.related_type && r.related_id != null ? `${r.related_type} #${r.related_id}` : '—'
    ) },
  ];

  return (
    <div className="or-page ad-page" data-screen="office-EmailLog">
      <PageHeader
        title="Email log"
        meta="Every email the system has sent — invoices, estimates, worker digests and tests."
      >
        <FwButton variant="ghost" icon="refresh" onClick={() => log.refetch()} loading={log.isFetching}>
          Refresh
        </FwButton>
      </PageHeader>

      <Toolbar>
        <FwField label="Category" htmlFor="or-email-category">
          <FwSelect id="or-email-category" value={category} onChange={(e) => { setCategory(e.target.value); setSelectedId(null); }}>
            {EMAIL_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Show" htmlFor="or-email-limit" hint={`The newest ${EMAIL_LIMIT_MAX} at most.`}>
          <FwSelect id="or-email-limit" value={String(limit)} onChange={(e) => { setLimit(Number(e.target.value)); setSelectedId(null); }}>
            {EMAIL_LIMITS.map((n) => <option key={n} value={String(n)}>{`Newest ${n}`}</option>)}
          </FwSelect>
        </FwField>
      </Toolbar>

      {log.error ? (
        <Notice tone="stop" title="The email log could not be loaded" data-testid="email-log-error">
          {serverSentence(log.error, 'Could not load the email log.')}
          {' '}Nothing below is a statement about what was or was not sent.
        </Notice>
      ) : null}
      {log.isPending && !log.error ? <Skeleton lines={6} /> : null}

      {/* Every figure and every row is inside this branch. A refused request
          reaches neither, so the screen cannot say "0 sent". */}
      {log.data ? (
        <>
          <div className="or-stats" data-testid="email-totals">
            <Stat label="In this view" value={String(rows.length)} />
            {statusTotals(rows).map((t) => (
              <Stat key={t.key} label={t.label} value={String(t.count)} tone={t.tone} />
            ))}
          </div>
          <Split
            main={(
              <DataTable
                caption="Emails the system has sent"
                columns={columns}
                rows={rows}
                onRowClick={(r) => setSelectedId(r.id)}
                empty={category ? 'No emails logged in this category.' : 'No emails logged yet.'}
              />
            )}
            aside={selected ? <EmailDetail key={selected.id} row={selected} onClose={() => setSelectedId(null)} /> : null}
          />
        </>
      ) : null}
    </div>
  );
}

// ── database + integrations status ──────────────────────────────────────────

/** counts keys in the order they are worth reading, with a label for each. */
const COUNT_LABELS = [
  ['users', 'People'],
  ['clients', 'Clients'],
  ['properties', 'Properties'],
  ['jobs', 'Jobs'],
  ['activeJobs', 'Active jobs'],
  ['archivedJobs', 'Archived jobs'],
  ['timeEntries', 'Clock entries'],
  ['timesheets', 'Timesheets'],
  ['paystubs', 'Paystubs'],
  ['invoices', 'Invoices'],
  ['media', 'Photos'],
  ['notifications', 'Notifications'],
];

/**
 * The counts to show, in a fixed order, skipping anything the server did not
 * send and keeping anything it sent that this list has not heard of.
 *
 * The skip is the load-bearing half: a missing key must produce NO tile, never
 * a tile reading 0. A real 0 (a fresh database with no invoices yet) is a
 * measurement and is shown, which is the case that stops this guard being
 * "hide every zero".
 */
export function countTiles(counts) {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return [];
  const tiles = [];
  const known = new Set();
  for (const [key, label] of COUNT_LABELS) {
    known.add(key);
    const v = counts[key];
    if (v === undefined || v === null) continue;
    tiles.push({ key, label, value: String(v) });
  }
  for (const key of Object.keys(counts)) {
    if (known.has(key)) continue;
    const v = counts[key];
    if (v === undefined || v === null) continue;
    tiles.push({ key, label: key, value: String(v) });
  }
  return tiles;
}

/**
 * The `database` block as label/value rows, again skipping what was not sent.
 * `tone` is only set where the value is a verdict rather than a fact.
 */
export function dbFacts(database) {
  const d = database && typeof database === 'object' ? database : null;
  if (!d) return [];
  const rows = [];
  if (d.integrityCheck !== undefined && d.integrityCheck !== null) {
    rows.push({ key: 'integrity', label: 'Integrity check', value: String(d.integrityCheck), tone: d.integrityCheck === 'ok' ? 'ok' : 'stop' });
  }
  if (d.foreignKeys !== undefined && d.foreignKeys !== null) {
    // CLAUDE.md: foreign keys are ENFORCED, and a deploy that quietly lost
    // that is the thing worth seeing from a screen rather than a shell.
    const on = Number(d.foreignKeys) === 1;
    rows.push({ key: 'fk', label: 'Foreign keys', value: on ? 'Enforced' : 'NOT enforced', tone: on ? 'ok' : 'stop' });
  }
  if (d.journalMode) rows.push({ key: 'journal', label: 'Journal mode', value: String(d.journalMode) });
  if (d.dbSizeMB) rows.push({ key: 'size', label: 'Database file', value: String(d.dbSizeMB) });
  if (d.walSizeMB) rows.push({ key: 'wal', label: 'Write-ahead log', value: String(d.walSizeMB) });
  const cp = d.walCheckpoint;
  if (cp && typeof cp === 'object' && (cp.log !== undefined || cp.checkpointed !== undefined)) {
    rows.push({
      key: 'checkpoint',
      label: 'Last checkpoint',
      value: `${cp.log ?? '—'} logged · ${cp.checkpointed ?? '—'} checkpointed${Number(cp.busy) ? ' · busy' : ''}`,
    });
  }
  return rows;
}

/**
 * What the screen says about a credential it is never allowed to show.
 *
 * Owner, 2026-09-14: *"Under system I see report a problem is configured, can
 * we have the token be there as well, so we know it was added…"* — so the
 * question this answers is "is one on file", not "what is it". The server
 * sends `{ present, source, hint, updatedAt, unreadable }` and no value at any
 * depth (server/utils/feedbackConfig.js); this turns that into a sentence.
 *
 * Exported and pure because the three states read almost the same and are
 * completely different things to tell an admin:
 *
 *   present + settings   somebody saved it here, and it can be changed here
 *   present + env        it is in the .env on the server, which is why saving
 *                        a new one is what changes it and editing .env is not
 *   unreadable           it IS saved and the server cannot decrypt it — the
 *                        state that must never render as "not configured",
 *                        because that sends an admin to add a token that is
 *                        already there
 */
export function tokenState(token) {
  if (!token || typeof token !== 'object') return null;
  if (token.unreadable) {
    return { tone: 'stop', label: 'Saved but unreadable', detail: 'The encryption key has changed since it was saved. Save it again in Settings → Reporting.' };
  }
  if (!token.present) {
    return { tone: 'warn', label: 'Not set', detail: 'Add one in Settings → Reporting.' };
  }
  const where = token.source === 'settings'
    ? 'Saved here, in Settings → Reporting'
    : 'Set on the server, in .env';
  const when = token.updatedAt ? ` · saved ${fmtDateTime(token.updatedAt)}` : '';
  return { tone: 'ok', label: 'On file', detail: `${where}${when}`, hint: token.hint || null };
}

function IntegrationsPanel() {
  // Cheap by construction — server/server.js says so and says why it is
  // separate from db-status: environment variables plus a couple of indexed
  // primary-key lookups. Safe to load with the page.
  const q = useOfficeQuery(['office', 'integrations'], '/admin/integrations');
  const list = q.data && q.data.integrations && typeof q.data.integrations === 'object'
    ? Object.entries(q.data.integrations)
    : null;

  return (
    <Panel title="Optional integrations">
      {q.error ? (
        <Notice tone="stop" title="The integration status could not be read" data-testid="integrations-error">
          {serverSentence(q.error, 'Could not read the integration status.')}
          {' '}Whether these are configured is unknown, not "not configured".
        </Notice>
      ) : null}
      {q.isPending && !q.error ? <Skeleton lines={3} /> : null}
      {list && list.length === 0 ? <p className="or-muted">The server reports no optional integrations.</p> : null}
      {list ? list.map(([key, it]) => (
        <div key={key} data-integration={key}>
          <DetailList>
            <DetailRow label={it.name || key}>
              <Pill tone={it.configured ? 'ok' : 'warn'}>{it.configured ? 'Configured' : 'Not configured'}</Pill>
            </DetailRow>
            {it.effect ? <DetailRow label="Effect">{it.effect}</DetailRow> : null}
            {Array.isArray(it.missing) && it.missing.length ? <DetailRow label="Missing">{it.missing.join(', ')}</DetailRow> : null}
            {it.repo ? <DetailRow label="Repo">{it.repo}</DetailRow> : null}
            {/* The token row. Rendered only when the server SENT a token
                block — an integration that has no credential must not grow a
                "Not set" line about a thing it does not have, and a server
                that is older than this screen must not either. */}
            {(() => {
              // One call, not five. `tokenState` is pure, so the repeats were
              // only waste — but five calls also read as five decisions when
              // there is one. Raised in review 2026-09-14.
              const tok = tokenState(it.token);
              return tok ? (
                <DetailRow label="Token">
                  <span className="ad-token" data-token-source={it.token.source || 'none'}>
                    <Pill tone={tok.tone}>{tok.label}</Pill>
                    {tok.hint ? <code className="ad-token__hint">{tok.hint}</code> : null}
                    <span className="ad-token__detail">{tok.detail}</span>
                  </span>
                </DetailRow>
              ) : null;
            })()}
          </DetailList>
          {it.fix ? <pre className="or-log">{it.fix}</pre> : null}
        </div>
      )) : null}
    </Panel>
  );
}

export function SystemStatus() {
  const role = useRole();
  // A plain label read, not a capability gate: both endpoints below are
  // `req.user.role !== 'admin'` on the server, so there is no resource/action
  // for useCan to ask about. Same shape as Settings.js and Users.js.
  const [ran, setRan] = useState(false);
  const db = useOfficeQuery(['office', 'db-status'], '/admin/db-status', { enabled: ran && role === 'admin' });

  if (role !== 'admin') {
    return (
      <div className="or-page ad-page" data-screen="office-SystemStatus">
        <PageHeader title="System" />
        <EmptyState icon="lock" title="Admins only">
          The database and integration status are limited to administrators.
        </EmptyState>
      </div>
    );
  }

  const tiles = db.data ? countTiles(db.data.counts) : [];
  const facts = db.data ? dbFacts(db.data.database) : [];

  return (
    <div className="or-page ad-page" data-screen="office-SystemStatus">
      <PageHeader title="System" meta="What is in the database, and which optional integrations are switched on.">
        <FwButton icon="activity" onClick={() => (ran ? db.refetch() : setRan(true))} loading={db.isFetching}>
          {ran ? 'Run again' : 'Run health check'}
        </FwButton>
      </PageHeader>

      <IntegrationsPanel />

      <Panel title="Database">
        {/* PRAGMA integrity_check walks the whole file. server/server.js:343
            keeps this behind a button for that reason and so does this screen —
            do not move it onto page load. */}
        {!ran ? (
          <p className="or-muted">
            The health check reads every row count and runs SQLite&apos;s integrity check, which walks the
            whole database file. Press Run health check when you want it.
          </p>
        ) : null}
        {db.error ? (
          <Notice tone="stop" title="The health check could not be run" data-testid="db-status-error">
            {serverSentence(db.error, 'Could not read the database status.')}
            {' '}No figures are shown below, because none were received.
          </Notice>
        ) : null}
        {db.isFetching && !db.data ? <Skeleton lines={5} /> : null}

        {/* Same rule as the email log: a figure only exists inside this branch. */}
        {db.data ? (
          <>
            {facts.length ? (
              <DetailList>
                {facts.map((f) => (
                  <DetailRow key={f.key} label={f.label}>
                    {f.tone ? <Pill tone={f.tone}>{f.value}</Pill> : f.value}
                  </DetailRow>
                ))}
              </DetailList>
            ) : null}
            {tiles.length ? (
              <div className="or-stats" data-testid="db-counts">
                {tiles.map((t) => <Stat key={t.key} label={t.label} value={t.value} />)}
              </div>
            ) : <p className="or-muted">The server sent no row counts.</p>}
          </>
        ) : null}
      </Panel>
    </div>
  );
}
